export type ReorderAction = "front" | "end" | "next" | "up" | "down";

/**
 * Target index for the DO `move` given a semantic reorder action, or `null`
 * when the action is a no-op (→ the button is disabled).
 *
 * `move` removes the item then re-inserts it, so when the item sits BEFORE the
 * currently-playing slide, removing it shifts `curIdx` down by one — hence the
 * `-(fromIdx < curIdx)` correction on "next".
 *
 * @param fromIdx item's current index in the full grid order (incl. hidden)
 * @param curIdx  currently-playing item's index, or -1 when none is playing
 * @param total   grid length (items.length)
 */
export function computeReorderIndex(
  action: ReorderAction,
  fromIdx: number,
  curIdx: number,
  total: number,
): number | null {
  if (total <= 1 || fromIdx < 0 || fromIdx >= total) return null;
  const last = total - 1;
  switch (action) {
    case "front":
      return fromIdx === 0 ? null : 0;
    case "end":
      return fromIdx === last ? null : last;
    case "next": {
      if (curIdx < 0 || fromIdx === curIdx) return null;
      const target = curIdx + 1 - (fromIdx < curIdx ? 1 : 0);
      return target === fromIdx ? null : target; // already right after current
    }
    case "up":
      return fromIdx <= 0 ? null : fromIdx - 1;
    case "down":
      return fromIdx >= last ? null : fromIdx + 1;
  }
}
